import { FC } from "react";
import { InputBaseComponentProps, TextField } from "@mui/material";

import { FormikProps } from "formik";
import { BrokerRequest, DesiredContactTime, FormOfAddress } from "@/types";
import { CheckboxLineWrapper, LineWrapper, LineWrapperDouble } from "./styles";

import { useTranslations } from "next-intl";
import { Checkbox, PhoneMaskInput, Select } from "@/components";

interface FormFieldsProps {
  formik: FormikProps<BrokerRequest>;
  isDisabled: boolean;
}

export const FormFields: FC<FormFieldsProps> = ({
  formik,
  isDisabled
}) => {
  const t = useTranslations();

  const noSafariAutofillInputProps = {
    autoComplete: 'off' as const,
  };

  const fastField = (name: keyof BrokerRequest) => ({
    ...formik.getFieldProps(name),
    error: !!formik.errors[name] && !!formik.touched[name],
    helperText:
      formik.touched[name] && formik.errors[name]
        ? (formik.errors[name] as string)
        : "",
  });

  return (
    <>
      <LineWrapper>
        <TextField
          label={t("housingCardDetails.form.title")}
          size="small"
          fullWidth
          disabled={isDisabled}
          {...fastField("title")}
          slotProps={{ htmlInput: noSafariAutofillInputProps }}
        />
      </LineWrapper>

      <LineWrapper>
        <TextField
          label={t("housingCardDetails.form.email")}
          size="small"
          type="email"
          fullWidth
          disabled={isDisabled}
          {...fastField("email")}
          slotProps={{ htmlInput: noSafariAutofillInputProps }}
        />
      </LineWrapper>

      <LineWrapperDouble>
        <Select
          label={t("housingCardDetails.form.formOfAddress")}
          name="formOfAddress"
          onChange={formik.handleChange}
          value={formik.values.formOfAddress}
          values={Object.values(FormOfAddress)}
          error={!!formik.errors.formOfAddress && formik.touched.formOfAddress}
          helperText={
            formik.touched.formOfAddress
              ? (formik.errors.formOfAddress as string)
              : ""
          }
          disabled={isDisabled}
        />
        <Select
          label={t("housingCardDetails.form.desiredContactTime")}
          name="desiredContactTime"
          onChange={formik.handleChange}
          value={formik.values.desiredContactTime}
          values={Object.values(DesiredContactTime)}
          error={
            !!formik.errors.desiredContactTime &&
            formik.touched.desiredContactTime
          }
          helperText={
            formik.touched.desiredContactTime
              ? (formik.errors.desiredContactTime as string)
              : ""
          }
          disabled={isDisabled}
        />
      </LineWrapperDouble>

      <LineWrapperDouble>
        <TextField
          label={t("housingCardDetails.form.name")}
          size="small"
          fullWidth
          disabled={isDisabled}
          {...fastField("name")}
          slotProps={{ htmlInput: noSafariAutofillInputProps }}
        />
        <TextField
          label={t("housingCardDetails.form.surname")}
          size="small"
          fullWidth
          disabled={isDisabled}
          {...fastField("surname")}
          slotProps={{ htmlInput: noSafariAutofillInputProps }}
        />
      </LineWrapperDouble>

      <LineWrapper>
        <TextField
          label={t("housingCardDetails.form.phone")}
          size="small"
          type="tel"
          fullWidth
          disabled={isDisabled}
          InputLabelProps={{ shrink: true }}
          InputProps={{ inputComponent: PhoneMaskInput as React.ElementType<InputBaseComponentProps> }}
          {...fastField("phone")}
          slotProps={{ htmlInput: noSafariAutofillInputProps }}
        />
      </LineWrapper>

      <LineWrapper>
        <TextField
          label={t("housingCardDetails.form.street")}
          size="small"
          fullWidth
          disabled={isDisabled}
          {...fastField("street")}
          slotProps={{ htmlInput: noSafariAutofillInputProps }}
        />
      </LineWrapper>

      <LineWrapperDouble>
        <TextField
          label={t("housingCardDetails.form.zipCode")}
          size="small"
          fullWidth
          disabled={isDisabled}
          {...fastField("zipCode")}
          slotProps={{ htmlInput: noSafariAutofillInputProps }}
        />
        <TextField
          label={t("housingCardDetails.form.ort")}
          size="small"
          fullWidth
          disabled={isDisabled}
          {...fastField("city")}
          slotProps={{ htmlInput: noSafariAutofillInputProps }}
        />
      </LineWrapperDouble>

      <CheckboxLineWrapper>
        <Checkbox
          label={t("housingCardDetails.visitRequest")}
          name="requestVisit"
          checked={formik.values.requestVisit}
          onChange={formik.handleChange}
          disabled={isDisabled}
        />
        <Checkbox
          label={t("housingCardDetails.callRequest")}
          name="requestCall"
          checked={formik.values.requestCall}
          onChange={formik.handleChange}
          disabled={isDisabled}
        />
        <Checkbox
          label={t("housingCardDetails.detailsRequest")}
          name="requestDetails"
          checked={formik.values.requestDetails}
          onChange={formik.handleChange}
          disabled={isDisabled}
        />
        {/*
          Todo: uncomment at 2.5 version release
        <Checkbox
          label={t("housingCardDetails.sendCopy")}
          name="requestSendCopyToMe"
          checked={formik.values.requestSendCopyToMe}
          onChange={formik.handleChange}
          disabled={isDisabled}
        /> */}
        <Checkbox
          label={t("housingCardDetails.similarItemsRequest")}
          name="requestSimilarObjects"
          checked={formik.values.requestSimilarObjects}
          onChange={formik.handleChange}
          disabled={isDisabled}
        />
        <Checkbox
          label={t("housingCardDetails.acceptGdpr")}
          name="gdprAccepted"
          checked={formik.values.gdprAccepted}
          onChange={formik.handleChange}
          error={!!formik.errors.gdprAccepted}
          disabled={isDisabled}
        />
      </CheckboxLineWrapper>

      <LineWrapper>
        <TextField
          label={t("housingCardDetails.form.personalMessage")}
          size="small"
          multiline
          minRows={3}
          fullWidth
          disabled={isDisabled}
          {...fastField("comment")}
          slotProps={{ htmlInput: noSafariAutofillInputProps }}
        />
      </LineWrapper>
    </>
  );
};